import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";

const TYPE_OPTS = [["lifestyle", "Lifestyle destination (Explore Mexico)"], ["route", "Service route (Where We Drive)"]];

export default function DestinationsManager() {
  const [rows, setRows] = useState([]);
  const [neu, setNeu] = useState({ name: "", region: "", blurb: "", image: "", type: "lifestyle" });

  const load = () => api.get("/admin/destinations").then((res) => setRows(res.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!neu.name) return;
    await api.post("/admin/destinations", { ...neu, order: rows.length });
    toast.success(neu.type === "route" ? "Route point added" : "Destination added");
    setNeu({ name: "", region: "", blurb: "", image: "", type: "lifestyle" });
    load();
  };

  const save = async (d) => {
    await api.put(`/admin/destinations/${d.id}`, { name: d.name, region: d.region, blurb: d.blurb, image: d.image, order: d.order, type: d.type || "lifestyle" });
    toast.success("Saved");
  };

  const remove = async (id) => {
    await api.delete(`/admin/destinations/${id}`);
    toast.success("Deleted");
    load();
  };

  const setField = (id, key, val) => setRows((arr) => arr.map((d) => (d.id === id ? { ...d, [key]: val } : d)));

  const lifestyle = rows.filter((d) => (d.type || "lifestyle") !== "route");
  const routes = rows.filter((d) => d.type === "route");

  const RowCard = ({ d }) => (
    <Card key={d.id}>
      <div className="flex gap-4">
        {d.image && <img src={d.image} alt={d.name} className="h-24 w-32 shrink-0 rounded-xl object-cover" />}
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <input data-testid={`dest-name-${d.id}`} className={FIELD} value={d.name} onChange={(e) => setField(d.id, "name", e.target.value)} />
            <select data-testid={`dest-type-${d.id}`} className={`${FIELD} w-64`} value={d.type || "lifestyle"} onChange={(e) => setField(d.id, "type", e.target.value)}>
              {TYPE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button data-testid={`dest-delete-${d.id}`} onClick={() => remove(d.id)} className="text-charcoal-400 hover:text-destructive shrink-0 px-2"><Trash2 size={16} /></button>
          </div>
          <input className={FIELD} value={d.region || ""} onChange={(e) => setField(d.id, "region", e.target.value)} placeholder="Region / tagline" />
          <input className={FIELD} value={d.image || ""} onChange={(e) => setField(d.id, "image", e.target.value)} placeholder="Image URL (optional for routes)" />
          <textarea rows={2} className={FIELD} value={d.blurb || ""} onChange={(e) => setField(d.id, "blurb", e.target.value)} />
          <button data-testid={`dest-save-${d.id}`} onClick={() => save(d)} className="rounded-full bg-sage px-5 py-2 text-sm text-white font-medium hover:bg-sage-hover">Save</button>
        </div>
      </div>
    </Card>
  );

  return (
    <div data-testid="admin-destinations">
      <SectionTitle title="Destinations & Service Routes" subtitle="Lifestyle destinations appear on Explore Mexico. Service routes appear in 'Where We Drive' and as trip planner suggestions — the two lists never mix." />

      <Card className="mb-6">
        <h3 className="font-medium text-charcoal-800 mb-4">Add an entry</h3>
        <div className="space-y-3">
          <select data-testid="dest-new-type" className={FIELD} value={neu.type} onChange={(e) => setNeu({ ...neu, type: e.target.value })}>
            {TYPE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input data-testid="dest-new-name" className={FIELD} placeholder={neu.type === "route" ? "Name (e.g. Laredo Border)" : "Name (e.g. Oaxaca)"} value={neu.name} onChange={(e) => setNeu({ ...neu, name: e.target.value })} />
          <input data-testid="dest-new-region" className={FIELD} placeholder="Region / tagline" value={neu.region} onChange={(e) => setNeu({ ...neu, region: e.target.value })} />
          <input data-testid="dest-new-image" className={FIELD} placeholder="Image URL (optional for routes)" value={neu.image} onChange={(e) => setNeu({ ...neu, image: e.target.value })} />
          <textarea data-testid="dest-new-blurb" rows={2} className={FIELD} placeholder="Short description" value={neu.blurb} onChange={(e) => setNeu({ ...neu, blurb: e.target.value })} />
          <button data-testid="dest-add" onClick={add} className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover">
            <Plus size={16} /> Add
          </button>
        </div>
      </Card>

      <h3 className="font-serif text-lg text-charcoal-800 mb-3">Lifestyle destinations (Explore Mexico)</h3>
      <div className="space-y-4 mb-8">{lifestyle.map((d) => <RowCard key={d.id} d={d} />)}</div>

      <h3 className="font-serif text-lg text-charcoal-800 mb-3">Service routes (Where We Drive)</h3>
      <div className="space-y-4" data-testid="admin-route-list">{routes.map((d) => <RowCard key={d.id} d={d} />)}</div>
    </div>
  );
}
